import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Box, Button, TextField, Typography, Grid, Paper,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions
} from '@mui/material';
import MainMeterSelector from './MainMeterSelector';
import SubMeterSelector from './SubMeterSelector';
import SiteService from './SiteService';
import TariffService from './TariffService';
import ConsumptionInputs from './ConsumptionInputs';

const MeterReadingForm = ({ isUpdate }) => {
  const { mainMeterId: paramMainMeterId, meterReadingId: paramMeterReadingId } = useParams();
  const [searchParams] = useSearchParams();
  const paramSubMeterId = searchParams.get('subMeterId');

  const [mainMeterId, setMainMeterId] = useState(paramMainMeterId || null);
  const [mainMeter, setMainMeter] = useState(null);
  const [subMeterId, setSubMeterId] = useState(paramSubMeterId || null);
  const [readingDate, setReadingDate] = useState('');
  const [indexTypes, setIndexTypes] = useState([]);
  const [indexValues, setIndexValues] = useState({});
  const [showForceDialog, setShowForceDialog] = useState(false);
  const [pendingForceRequest, setPendingForceRequest] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');

  const navigate = useNavigate();

  useEffect(() => {
    if (!readingDate) {
      const today = new Date().toISOString().split("T")[0];
      setReadingDate(today);
    }
  }, [readingDate]);

  useEffect(() => {
    if (isUpdate && mainMeterId && subMeterId && paramMeterReadingId) {
      SiteService.getMeterReading(mainMeterId, subMeterId, paramMeterReadingId)
        .then((res) => {
          const data = res.data;

          if (data.readingTime) {
            setReadingDate(data.readingTime.split('T')[0]);
          }

          if (data.meterReadingDetails) {
            const values = {};
            data.meterReadingDetails.forEach((detail) => {
              values[detail.indexTypeDto.indexType] = detail.index.toString().replace('.', ',');
            });
            setIndexValues(values);
          }
        })
        .catch((err) => {
          console.error('Sayaç okuma verisi alınamadı:', err);
          alert('Veriler alınamadı.');
        });
    }
  }, [isUpdate, mainMeterId, subMeterId, paramMeterReadingId]);


  useEffect(() => {
    if (mainMeter?.tariff?.tariffId) {
      TariffService.getMeterReadingIndexTypesByTariffId(mainMeter.tariff.tariffId).then((res) => {
        setIndexTypes(res.data);
      });
    }
  }, [mainMeter?.tariff?.tariffId]);

  const handleValueChange = (type, value) => {
    setIndexValues((prev) => ({ ...prev, [type]: value }));
  };

  const saveOrUpdateInvoice = (force = false) => {
    const meterReadingDetails = Object.entries(indexValues)
      .filter(([_, value]) => value !== '')
      .map(([typeKey, value]) => {
        const typeObj = indexTypes.find(t => t.indexType === typeKey);
        return {
          indexType: typeObj?.indexType ,
          index: parseFloat(value.replace(',', '.'))
        };
      });


    const meterReading = {
      
      readingTime: readingDate ? `${readingDate}T12:00:00` : null,
      meterReadingDetails,
      force
    };

    const action = isUpdate
      ? SiteService.updateMeterReading(mainMeterId, subMeterId, paramMeterReadingId, meterReading)
      : SiteService.createMeterReading(mainMeterId, subMeterId, meterReading);

    action
      .then(() => {
        navigate(`/meter-readings/${mainMeterId}`);
      })
      .catch((error) => {
        const status = error?.response?.status;
        const errMsg = error?.response?.data?.message || 'Bilinmeyen bir hata oluştu.';

        if (status === 400 && errMsg.includes('düşük')) {
          setErrorMessage(errMsg);
          setPendingForceRequest(() => () => saveOrUpdateInvoice(true));
          setShowForceDialog(true);
        } else {
          alert(errMsg);
        }
      });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    saveOrUpdateInvoice(false);
  };

  return (
    <Box sx={{ p: 4 }}>
      <Typography variant="h4" gutterBottom align="center">
        Sayaç Okuma Sonucu Girişi
      </Typography>

      <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <SubMeterSelector
              mainMeterId={mainMeterId}
              setMainMeterId={setMainMeterId}
              setMainMeter={setMainMeter}
              subMeterId={subMeterId}
              setSubMeterId={setSubMeterId}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              label="Sayaç Okuma Tarihi"
              type="date"
              fullWidth
              value={readingDate}
              onChange={(e) => setReadingDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
        </Grid>
      </Paper>

      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Endeks Değerleri
        </Typography>

        <ConsumptionInputs
          validIndexTypes={indexTypes.filter(it => it.indexType !== 'T0' && it.indexType !== 'P' && it.indexType !== 'D')}
          values={indexValues}
          onChange={setIndexValues}
          decimalSeparator=","
          maxDecimalDigits={3}
          mode="reading"
        />
      </Paper>

      <Box textAlign="center">
        <Button
          variant="contained"
          color="primary"
          size="large"
          onClick={handleSubmit}
          disabled={!mainMeterId || !subMeterId || !readingDate}
        >
          {isUpdate ? 'Güncelle' : 'Kaydet'}
        </Button>
      </Box>

      <Dialog open={showForceDialog} onClose={() => setShowForceDialog(false)}>
        <DialogTitle>Uyarı</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {errorMessage}
            <br /><br />
            Yine de devam etmek istiyor musunuz?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowForceDialog(false)} color="secondary">
            Vazgeç
          </Button>
          <Button
            onClick={() => {
              setShowForceDialog(false);
              if (pendingForceRequest) {
                pendingForceRequest();
              }
            }}
            color="primary"
          >
            Evet, Devam Et
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MeterReadingForm;
